import { useEffect } from "react";

/**
 * Manage per-route meta tags (title, description, OG image, canonical).
 *
 * Usage:
 *   <SeoMeta
 *     title="Zateplenie fasády | SuperMajstri"
 *     description="Profesionálne zateplenie fasády..."
 *     image="https://supermajstri.sk/og/zateplenie.png"
 *     url="https://supermajstri.sk/sluzby/zateplenie"
 *   />
 *
 * Restores the previous title on unmount and cleans up tags it created.
 */
export default function SeoMeta({ title, description, image, ogImage, url, canonical, type = "article" }) {
  useEffect(() => {
    // Accept both `url`/`canonical` and `image`/`ogImage` for backwards compatibility.
    const canonicalHref = canonical || url;
    const ogImageSrc = image || ogImage;

    const prevTitle = document.title;
    if (title) document.title = title;

    const created = [];
    const upsert = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      let didCreate = false;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        didCreate = true;
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", value);
      created.push({ el, didCreate, attr, key, prev });
    };

    upsert("name", "description", description);
    upsert("property", "og:title", title);
    upsert("property", "og:description", description);
    upsert("property", "og:image", ogImageSrc);
    upsert("property", "og:image:width", ogImageSrc ? "1200" : null);
    upsert("property", "og:image:height", ogImageSrc ? "630" : null);
    upsert("property", "og:url", canonicalHref);
    upsert("property", "og:type", type);
    upsert("name", "twitter:card", ogImageSrc ? "summary_large_image" : null);
    upsert("name", "twitter:title", title);
    upsert("name", "twitter:description", description);
    upsert("name", "twitter:image", ogImageSrc);

    let canonEl = null;
    let createdCanonical = false;
    if (canonicalHref) {
      canonEl = document.head.querySelector('link[rel="canonical"]');
      const prev = canonEl ? canonEl.getAttribute("href") : null;
      if (!canonEl) {
        canonEl = document.createElement("link");
        canonEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonEl);
        createdCanonical = true;
      }
      canonEl.setAttribute("href", canonicalHref);
      canonEl._prev = prev;
    }

    return () => {
      document.title = prevTitle;
      created.forEach(({ el, didCreate, prev }) => {
        if (didCreate) {
          el.remove();
        } else if (prev != null) {
          el.setAttribute("content", prev);
        }
      });
      if (canonEl) {
        if (createdCanonical) canonEl.remove();
        else if (canonEl._prev) canonEl.setAttribute("href", canonEl._prev);
      }
    };
  }, [title, description, image, ogImage, url, canonical, type]);

  return null;
}
